import { Box, Typography, Container, Button, Paper, Stack, Divider, Grid, Chip, Snackbar, Alert, Avatar } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { CalendarToday, AccessTime, Videocam, Business, Phone, ArrowForward, CheckCircle, Schedule } from '@mui/icons-material';
import { useRef, useState } from 'react';

const AppointmentBooking = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [selectedType, setSelectedType] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const meetingTypes = [
    { 
      icon: <Business sx={{ fontSize: 28 }} />, 
      title: 'In-Person Meeting', 
      duration: '1 hour', 
      location: 'Johannesburg HQ',
      description: 'Face-to-face strategic discussion at our headquarters',
      availability: 'Limited slots',
      popular: true
    },
    { 
      icon: <Videocam sx={{ fontSize: 28 }} />, 
      title: 'Video Conference', 
      duration: '30-60 min', 
      location: 'Zoom / Microsoft Teams',
      description: 'Remote meeting via Zoom or Microsoft Teams',
      availability: 'Weekly slots',
      popular: false,
      platforms: ['Zoom', 'Microsoft Teams']
    },
    { 
      icon: <Phone sx={{ fontSize: 28 }} />, 
      title: 'WhatsApp Call', 
      duration: '15-30 min', 
      location: 'WhatsApp Video/Audio',
      description: 'Quick consultation via WhatsApp video or voice call',
      availability: 'Daily slots',
      popular: false,
      platforms: ['WhatsApp Video', 'WhatsApp Voice']
    },
  ];

  const sendEmailNotification = async (meetingTitle) => {
    const emailData = {
      to_email: 'john.asfar@worldwidecorporation.com',
      to_name: 'John Asfar',
      meeting_type: meetingTitle,
      meeting_date: new Date().toLocaleString(),
      user_agent: navigator.userAgent,
    };

    // Using mailto: link (opens email client)
    const mailtoLink = `mailto:john.asfar@worldwidecorporation.com?subject=Meeting%20Request%3A%20${encodeURIComponent(meetingTitle)}&body=${encodeURIComponent(
      `Meeting Type: ${meetingTitle}\nRequest Time: ${new Date().toLocaleString()}\n\nThis meeting request was submitted through the appointment booking system.\n\nPlease follow up to confirm availability and preferred platform (Zoom, Teams, or WhatsApp).`
    )}`;
    
    window.location.href = mailtoLink;
    return { success: true, method: 'mailto' };
  };

  const handleSelectMeeting = async (meetingTitle) => {
    setSelectedType(meetingTitle);
    setIsSending(true);
    
    try {
      // Send email notification to john.asfar@worldwidecorporation.com
      const result = await sendEmailNotification(meetingTitle);
      
      if (result.success) {
        setShowSuccess(true);
      } else {
        // Show error message if email fails
        alert('Failed to send meeting request. Please try again or contact directly.');
      }
    } catch (error) {
      console.error('Error in meeting selection:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSending(false);
      setTimeout(() => {
        setSelectedType(null);
      }, 1000);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15, duration: 0.6 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
    hover: { scale: 1.02, transition: { duration: 0.2 } },
  };

  const rightCardVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, delay: 0.2 } },
    hover: { scale: 1.02, transition: { duration: 0.2 } },
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 10, md: 18 },
        background: 'linear-gradient(135deg, #0a0a0a 0%, #0f0f0f 50%, #0a0a0a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#C8A96B',
                letterSpacing: 6,
                mb: 2,
                textAlign: 'center',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              SCHEDULE A MEETING
            </Typography>

            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Book Executive Time
            </Typography>

            <Typography
              sx={{
                textAlign: 'center',
                maxWidth: '650px',
                mx: 'auto',
                color: '#ccc',
                mb: 8,
                fontSize: '1.05rem',
                lineHeight: 1.6,
              }}
            >
              Schedule a direct conversation with John Asfar. Select your preferred 
              meeting format and get personalized access to executive leadership.
            </Typography>
          </motion.div>

          <Grid container spacing={5}>
            {/* Left Column - Meeting Types */}
            <Grid item xs={12} md={6}>
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                whileHover="hover"
              >
                <Paper
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'linear-gradient(135deg, #0d0d0d 0%, #0a0a0a 100%)',
                    border: '1px solid rgba(200,169,107,0.2)',
                    borderRadius: '24px',
                    height: '100%',
                    transition: 'all 0.3s',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: '#C8A96B',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Schedule />
                    Select Meeting Format
                  </Typography>

                  <Stack spacing={3}>
                    {meetingTypes.map((type, index) => (
                      <Box key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 2 }}>
                          <Box
                            sx={{
                              color: '#C8A96B',
                              p: 1,
                              borderRadius: '12px',
                              background: 'rgba(200,169,107,0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {type.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                                {type.title}
                              </Typography>
                              {type.popular && (
                                <Chip
                                  label="Popular"
                                  size="small"
                                  sx={{
                                    background: '#C8A96B',
                                    color: '#000',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                              {type.duration} • {type.location}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#aaa', mb: 1.5 }}>
                              {type.description}
                            </Typography>
                            {type.platforms && (
                              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                {type.platforms.map((platform, idx) => (
                                  <Chip
                                    key={idx}
                                    label={platform}
                                    size="small"
                                    sx={{
                                      background: 'rgba(200,169,107,0.15)',
                                      color: '#C8A96B',
                                      fontSize: '0.7rem',
                                      height: '20px',
                                    }}
                                  />
                                ))}
                              </Stack>
                            )}
                            <Typography variant="caption" sx={{ color: '#C8A96B' }}>
                              {type.availability}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleSelectMeeting(type.title)}
                            disabled={selectedType === type.title || isSending}
                            sx={{
                              borderColor: '#C8A96B',
                              color: '#C8A96B',
                              borderRadius: '20px',
                              px: 2.5,
                              '&:hover': {
                                borderColor: '#D6B57B',
                                background: 'rgba(200,169,107,0.1)',
                              },
                            }}
                          >
                            {selectedType === type.title ? <CheckCircle sx={{ fontSize: 18 }} /> : 
                             isSending && selectedType === type.title ? 'Sending...' : 'Select'}
                          </Button>
                        </Box>
                        {index < meetingTypes.length - 1 && (
                          <Divider sx={{ borderColor: 'rgba(200,169,107,0.1)', my: 2 }} />
                        )}
                      </Box>
                    ))}
                  </Stack>

                  {/* Meeting Benefits */}
                  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(200,169,107,0.1)' }}>
                    <Typography variant="caption" sx={{ color: '#777', display: 'block', textAlign: 'center' }}>
                      ✓ No intermediaries • ✓ Direct access • ✓ 24hr confirmation
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            {/* Right Column - Calendar & CTA */}
            <Grid item xs={12} md={6}>
              <motion.div
                variants={rightCardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                whileHover="hover"
              >
                <Paper
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'linear-gradient(135deg, #111 0%, #0a0a0a 100%)',
                    border: '1px solid rgba(200,169,107,0.3)',
                    borderRadius: '24px',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-50%',
                      right: '-50%',
                      width: '200px',
                      height: '200px',
                      background: 'radial-gradient(circle, rgba(200,169,107,0.05) 0%, transparent 70%)',
                      borderRadius: '50%',
                    },
                  }}
                >
                  <CalendarToday sx={{ fontSize: 70, color: '#C8A96B', mb: 2 }} />
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#fff' }}>
                    Ready to Connect?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 3, lineHeight: 1.6 }}>
                    All meetings include direct executive access with no intermediaries.
                    Available on Zoom, Microsoft Teams, and WhatsApp.
                    Expect confirmation within 24 hours of scheduling.
                  </Typography>

                  {/* Quick Stats */}
                  <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#C8A96B', fontWeight: 700 }}>24h</Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>Response</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#C8A96B', fontWeight: 700 }}>100%</Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>Direct Access</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#C8A96B', fontWeight: 700 }}>Global</Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>Time Zones</Typography>
                    </Box>
                  </Stack>

                  {/* Platform Icons */}
                  <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                    <Chip
                      label="Zoom"
                      size="small"
                      sx={{
                        background: 'rgba(200,169,107,0.1)',
                        color: '#C8A96B',
                        border: '1px solid rgba(200,169,107,0.2)',
                      }}
                    />
                    <Chip
                      label="Teams"
                      size="small"
                      sx={{
                        background: 'rgba(200,169,107,0.1)',
                        color: '#C8A96B',
                        border: '1px solid rgba(200,169,107,0.2)',
                      }}
                    />
                    <Chip
                      label="WhatsApp"
                      size="small"
                      sx={{
                        background: 'rgba(200,169,107,0.1)',
                        color: '#C8A96B',
                        border: '1px solid rgba(200,169,107,0.2)',
                      }}
                    />
                  </Stack>

                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={() => handleSelectMeeting('Schedule')}
                    disabled={isSending}
                    sx={{
                      background: 'linear-gradient(135deg, #C8A96B, #b89750)',
                      color: '#000',
                      py: 1.8,
                      fontSize: '1rem',
                      fontWeight: 700,
                      borderRadius: '40px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #D6B57B, #C8A96B)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {isSending ? 'Sending Request...' : 'View Available Time Slots'}
                  </Button>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>

          {/* Trust Badges */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mt: 8,
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 2, md: 5 },
                flexWrap: 'wrap',
              }}
            >
              {['Secure Booking', 'Confidential', 'No Obligation', 'Global Reach', 'Zoom • Teams • WhatsApp'].map((badge, idx) => (
                <Chip
                  key={idx}
                  label={badge}
                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                  sx={{
                    background: 'rgba(200,169,107,0.1)',
                    color: '#C8A96B',
                    border: '1px solid rgba(200,169,107,0.2)',
                    '& .MuiChip-icon': { color: '#C8A96B' },
                  }}
                />
              ))}
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ background: '#C8A96B', color: '#000', fontWeight: 600 }}>
          Meeting request sent to John Asfar! You'll receive confirmation within 24 hours with Zoom, Teams, or WhatsApp details.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppointmentBooking;